import * as React from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import { Form, Button, Modal, Grid, List } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { CSVReader } from 'react-papaparse';

// Form
import { RenderTextArea } from 'forms';

// Selectors
// @ts-ignore
import { selectWorkTypesKeyedByName } from 'services/workTypes/selectors';
import { selectPeopleKeyedByDatacomId } from 'services/people/selectors';
import {
    selectActiveEntitiesArray,
    selectEntityPathNamesKeyed
} from 'services/entities/selectors';

import { makeCsvEntriesValidator } from './validate';
import { TimesheetCSVEntry } from './types';

export interface OwnProps {
    title: string;
    onClose: () => void;
    onSubmit: (timesheetEntries: any) => void;
    defaultValues: { timesheetId?: number };
}

const TimesheetImportTimesheetEntryForm = (
    props: OwnProps
): React.ReactElement => {
    const {
        title,
        onClose,
        defaultValues: { timesheetId },
        onSubmit
    } = props;

    // Mapped data to check existence of fields
    const workTypesKeyedByName: any = useSelector(selectWorkTypesKeyedByName);
    const peopleKeyedByDatacomId = useSelector(selectPeopleKeyedByDatacomId);
    const activeEntities = useSelector(selectActiveEntitiesArray);
    const entityPathName: any = useSelector(selectEntityPathNamesKeyed);

    const combinedValidator = makeCsvEntriesValidator(
        workTypesKeyedByName,
        peopleKeyedByDatacomId,
        entityPathName
    );

    const submitValues = ({
        entries
    }: {
        entries: TimesheetCSVEntry[];
    }): void => {
        const mappedEntries = entries.map((item) => {
            const { personReference, taskName, area, ...rest } = item;

            const workType = workTypesKeyedByName[taskName];
            const person = peopleKeyedByDatacomId[personReference];
            const foundArea = activeEntities.find(
                (entity) => entity.pathNames === area
            );
            const entityCompositeKey = foundArea
                ? `${foundArea.id}.${foundArea.entityTypeId}`
                : null;
            const workTypeCompositeKey = `${workType.id}.${workType.isPieceRate}.${workType.entityTypeId}`;

            return {
                timesheetId,
                workTypeCompositeKey,
                personId: person.id,
                entityCompositeKey,
                ...rest
            };
        });

        onSubmit(mappedEntries);
        onClose();
    };

    return (
        <React.Fragment>
            <Modal.Header>{title}</Modal.Header>
            <Modal.Content>
                <FinalForm
                    onSubmit={submitValues}
                    mutators={{
                        setEntries: ([rows], state, utils) => {
                            utils.changeValue(state, 'entries', () => rows);
                        },
                        clearEntries: (args, state, utils) => {
                            utils.changeValue(state, 'entries', () => []);
                        }
                    }}
                    render={({ form, handleSubmit }): React.ReactElement => {
                        return (
                            <Form autoComplete="off" onSubmit={handleSubmit}>
                                <Grid>
                                    <Grid.Row>
                                        <Grid.Column width={16}>
                                            <span>Columns (* = required):</span>
                                            <List as="ul">
                                                <List.Item as="li">
                                                    date*
                                                </List.Item>
                                                <List.Item as="li">
                                                    startTime*
                                                </List.Item>
                                                <List.Item as="li">
                                                    endTime*
                                                </List.Item>
                                                <List.Item as="li">
                                                    personReference*
                                                </List.Item>
                                                <List.Item as="li">
                                                    taskName*
                                                </List.Item>
                                                <List.Item as="li">
                                                    area*
                                                </List.Item>
                                                <List.Item as="li">
                                                    rate
                                                </List.Item>
                                                <List.Item as="li">
                                                    units
                                                </List.Item>
                                            </List>
                                        </Grid.Column>
                                    </Grid.Row>
                                    <Grid.Row>
                                        <Grid.Column width={16}>
                                            <CSVReader
                                                onDrop={(parsedRows) => {
                                                    const rows = parsedRows.map(
                                                        (parsedRow) =>
                                                            parsedRow.data
                                                    );

                                                    form.mutators.setEntries(
                                                        rows
                                                    );
                                                }}
                                                noDrag
                                                addRemoveButton
                                                onRemoveFile={() => {
                                                    form.mutators.clearEntries();
                                                }}
                                                config={{
                                                    skipEmptyLines: true,
                                                    header: true
                                                }}
                                            >
                                                <span>Click to upload.</span>
                                            </CSVReader>
                                        </Grid.Column>
                                    </Grid.Row>
                                    <Grid.Row>
                                        <Grid.Column width={16}>
                                            <Field
                                                style={{ display: 'none' }}
                                                name="entries"
                                                component={
                                                    RenderTextArea as any
                                                }
                                                validate={combinedValidator}
                                            />
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                                <Grid>
                                    <Grid.Column width={8}>
                                        <Button
                                            type="button"
                                            onClick={onClose}
                                            fluid
                                            basic
                                            primary
                                        >
                                            Cancel
                                        </Button>
                                    </Grid.Column>
                                    <Grid.Column width={8}>
                                        <Button type="submit" primary fluid>
                                            Submit
                                        </Button>
                                    </Grid.Column>
                                </Grid>
                            </Form>
                        );
                    }}
                />
            </Modal.Content>
        </React.Fragment>
    );
};

export default TimesheetImportTimesheetEntryForm;
